/**
 * Commission report maths — the figures derived from an entered commission and
 * the rep's NetSuite sales for a pay period.
 *
 * The report shows five numbers per rep (Michael, 2026-09-10): Sales, Tons Sold,
 * Commission, Comm/Ton and % Commission. Only the first three are DATA —
 * commission is entered by hand, sales and tonnage are read from NetSuite. The
 * last two are derived, and they live here rather than inline in the table so
 * the browser and any future export/report agree by construction instead of by
 * being written twice.
 *
 * PURE and dependency-free, matching the other shared report modules.
 *
 * WHY EVERY DIVISION RETURNS null RATHER THAN 0 OR NaN. A rep with no tonnage in
 * a period is a real and ordinary case — a commission paid on a week with no
 * shipments, a correction, a new rep. Dividing by it yields Infinity, and
 * rendering that as "0.00" would state a rate of zero, which is a different and
 * false claim. `null` means "cannot be computed", and the caller renders an
 * em-dash. A commission report is read by the people being paid from it, so a
 * figure it cannot support must look absent, never like a number.
 */
/**
 * NetSuite internal id of the SALES department (`13`).
 *
 * Who may be given a commission: only the sales team appears in the New
 * Commission entry picker (Michael, 2026-09-11) — a commission for a warehouse
 * hand is a typo, and the roster is 150+ people to scroll past.
 *
 * Pinned by NETSUITE ID rather than by the name "Sales" so renaming the
 * department in NetSuite cannot silently empty the picker. Same reasoning, and
 * the same shape, as {@link DRIVING_NS_DEPARTMENT_ID} in driver-log.ts.
 *
 * It lives in shared because the API filters the picker with it and the report
 * is read against it; two local copies would drift, and the failure would be
 * quiet — a rep missing from the dropdown with nothing to indicate why.
 */
export const SALES_NS_DEPARTMENT_ID = '13';
/**
 * Days the commission week sits BEFORE the payroll week.
 *
 * Commission does not follow the payroll calendar (Michael, 2026-09-11).
 * Payroll runs Wed→Tue; commission is calculated Monday→Sunday, which is the
 * same seven days shifted back two. Confirmed against the NetSuite report Boe
 * runs: for the payroll week Wed 2026-09-02 → Tue 2026-09-08, the commission
 * window was Mon 2026-08-31 → Sun 2026-09-06.
 *
 * Expressed as an OFFSET from the payroll period rather than as its own weekday
 * arithmetic, so the two calendars cannot drift apart if the payroll week ever
 * moves.
 */
export const COMMISSION_WEEK_OFFSET_DAYS = -2;
/** The Monday–Sunday window a payroll period's commission is calculated over. */
export function commissionWindowFor(period) {
    return {
        startDate: shiftIsoDate(period.startDate, COMMISSION_WEEK_OFFSET_DAYS),
        endDate: shiftIsoDate(period.endDate, COMMISSION_WEEK_OFFSET_DAYS),
    };
}
/** Shift an ISO date by whole days, in UTC so it cannot drift across a zone. */
function shiftIsoDate(date, days) {
    const ms = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10))) +
        days * 86_400_000;
    return new Date(ms).toISOString().slice(0, 10);
}
/**
 * Internal id of the NetSuite saved search "JM - Commission Report (all reps)".
 *
 * WLX READS this search rather than calculating commission itself. The rate
 * varies per rep — 6.42/ton for one and 11.41 for another in the same week — and
 * those rules live in the search, so a second implementation would drift from the
 * first and the drift would land in someone's pay (Michael, 2026-09-11).
 *
 * ⚠️ ENVIRONMENT-SPECIFIC. This id is the PRODUCTION account's; a sandbox rebuilt
 * from scratch would number its searches differently. Pinned here for the same
 * reason as SALES_NS_DEPARTMENT_ID, and it is the one line to change.
 */
export const JM_COMMISSION_SEARCH_ID = '3968';
/**
 * Where each figure sits in that search's result columns.
 *
 * 🔥 POSITION IS THE ONLY THING THAT SEPARATES 2 FROM 3. Both are unnamed formula
 * columns, so NetSuite labels both "Formula (Currency)" — total commission, and
 * commission per ton. Reading by label collapsed them and the COMMISSION AMOUNT
 * silently disappeared. The label and summary are asserted alongside the index so
 * a reordered search fails loudly instead of paying out the per-ton rate.
 */
export const JM_COMMISSION_COLUMNS = {
    salesRep: { index: 0, label: 'Sales Rep', summary: 'GROUP' },
    tons: { index: 1, label: 'Formula (Numeric)', summary: 'SUM' },
    /** THE number that posts to the paycheck. */
    commission: { index: 2, label: 'Formula (Currency)', summary: 'SUM' },
    commissionPerTon: { index: 3, label: 'Formula (Currency)', summary: 'SUM' },
};
/**
 * Read one cell by position, asserting it is the column we think it is.
 *
 * Throws rather than returning a wrong number: this feeds a paycheck, and a
 * silently mis-read column is the failure mode that already happened once.
 */
export function readCommissionCell(cells, column) {
    const cell = cells[column.index];
    if (cell === undefined) {
        throw new Error(`Commission search returned ${String(cells.length)} columns; expected one at index ${String(column.index)} (${column.label})`);
    }
    if (cell.label !== column.label || cell.summary !== column.summary) {
        throw new Error(`Commission search column ${String(column.index)} is "${String(cell.label)}"/"${String(cell.summary)}", expected "${column.label}"/"${column.summary}" — the search has been reordered or edited`);
    }
    return cell;
}
/**
 * NetSuite payroll item a commission posts under: 9, "COMMISSION - SALES".
 *
 * Read off Tyler Ford's committed paycheck 12387549 rather than assumed. Unlike
 * worked time — where the earning is resolved per employee and department — every
 * commission uses this ONE item, so there is nothing to resolve.
 *
 * It is `kind: 'bonus'` in the earning map, which matters: bonus is NOT
 * rate-based, so the line carries a flat `amount`. A bare amount against a
 * rate-based item saves as ZERO — success reported, nothing paid.
 */
export const COMMISSION_NS_PAYROLL_ITEM_ID = '9';
/**
 * Divide, or say you cannot.
 *
 * Guards the three ways this goes wrong with real data: a zero denominator
 * (Infinity), a negative-or-zero denominator from a credit-heavy week, and a
 * non-finite input arriving from a NetSuite figure that failed to parse.
 */
function safeRatio(numerator, denominator) {
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) {
        return null;
    }
    if (denominator === 0) {
        return null;
    }
    return numerator / denominator;
}
/** The derived columns for one commission row. */
export function deriveCommission(row) {
    return {
        commissionPerTon: safeRatio(row.commission, row.tons),
        commissionRateOfSales: safeRatio(row.commission, row.sales),
    };
}
/**
 * Period totals across every rep on the report.
 *
 * The derived totals are computed from the SUMMED figures, never by averaging
 * the per-rep rates. Averaging rates weights a rep with two tons the same as a
 * rep with three hundred, which is simply the wrong number — the total rate is
 * total commission over total tons.
 */
export function totalCommission(rows) {
    const commission = rows.reduce((sum, r) => sum + (Number.isFinite(r.commission) ? r.commission : 0), 0);
    const sales = rows.reduce((sum, r) => sum + (Number.isFinite(r.sales) ? r.sales : 0), 0);
    const tons = rows.reduce((sum, r) => sum + (Number.isFinite(r.tons) ? r.tons : 0), 0);
    return { commission, sales, tons, ...deriveCommission({ commission, sales, tons }) };
}
//# sourceMappingURL=commission.js.map